import React from 'react'
import { Card, Col, Row, Stack } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useToggle } from 'react-use'
import difference from 'lodash/difference'
import isEmpty from 'lodash/isEmpty'
import type {
  AddChatCcArgs,
  AssignChatArgs,
  Chat,
  RemoveChatCcArgs,
  RouteDef,
  UpdateChatRemarksArgs,
  UpdateChatTagsArgs,
} from '@ztechapps/common.contract'
import type { CustomerWidgetProps } from '@ztechapps/webui.contract'
import { ChatCancelReasonEnum, ChatStatusEnum } from '@ztechapps/common.contract'
import { useDocument, useError, useMutation, useRole } from '@ztechapps/common.hook'
import { isValidEmail } from '@ztechapps/common.utils'
import { LocalDateTime } from '@ztechapps/webui.date'
import type { SelectOption } from '@ztechapps/webui.input'
import { InlineDisplayContent, SelectInlineInput, TagsInlineInput, TextAreaInlineInput } from '@ztechapps/webui.input'
import { DataSuspense, EmptyContent, ShowIf } from '@ztechapps/webui.util'
import { HoverContainer, HoverMenuContainer } from '@ztechapps/webui.menu'
import { BusyButtonLink } from '@ztechapps/webui.button'
import { chatPermissions, useChatMessage } from '@ztechapps/chat.common'
import { useInject, useInjectComponent, WebuiIocKey } from '@ztechapps/webui.contract'
import { useConversationContext } from './Context'
import { ChatAttachments } from '../Chat/ChatAttachments'
import { collectChatAttachments } from '../Chat/collectChatAttachments'
import { AssigneeSelect } from './AssigneeSelect'
import { AssignToMeLink } from './AssignToMeLink'
import { ConversationCustomerPopover } from './ConversationCustomerPopover'
import { conversationListFieldConfig } from '../Inbox/constants'
import {
  addChatCcMutation,
  assignChatMutation,
  conversationDetailsQuery,
  removeChatCcMutation,
  updateChatRemarksMutation,
  updateChatTagsMutation,
} from '../Inbox/graphql'

export type ConversationDetailsPanelProps = {
  conversationId?: string
  renderDocumentCell?: (chat: { document?: Chat['document']; metadata?: Chat['metadata'] }) => React.ReactNode
}

// One wording per cancel reason, naming what decided the cancellation — the mail provider's
// verdict and an agent's marking stay distinguishable. Defined once here, the rail's only
// reader, rather than retyped per surface.
const chatCancelReasonDescription: Record<ChatCancelReasonEnum, string> = {
  [ChatCancelReasonEnum.AgentCancellation]: 'Cancelled by an agent',
  [ChatCancelReasonEnum.AgentSpamMarking]: 'Marked as spam by an agent',
  [ChatCancelReasonEnum.MailProviderVerdict]: 'Flagged as spam by the mail provider',
  [ChatCancelReasonEnum.SpamListMatch]: 'Sender is on the spam-sender list',
  [ChatCancelReasonEnum.MergeAbsorption]: 'Merged into another conversation',
}

// Without a host cell the document row falls back to the stored code — the inbox host
// supplies its own document links while other mounts have none to give.
const defaultRenderDocumentCell: NonNullable<ConversationDetailsPanelProps['renderDocumentCell']> = (chat) =>
  chat?.document?.code ?? '—'

type StackedRowProps = {
  children: React.ReactNode
}

// A many-entry row — Attachments, Copied to — stacks label-over-value across the full width, the inputs'
// own stacked mode, inside LabelValueBlock's own `Row className="py-2 border-bottom"` and `Col`
// (libs/webui/util/src/Block/LabelValueBlock.tsx:17-18,31,37): the Row's gutter margins run the divider as
// wide as the other rows', the Col's padding keeps the content aligned with theirs.
const StackedRow = ({ children }: StackedRowProps) => (
  <Row className="py-2 border-bottom">
    <Col>{children}</Col>
  </Row>
)

export const ConversationDetailsPanel = ({
  conversationId: conversationIdProp,
  renderDocumentCell = defaultRenderDocumentCell,
}: ConversationDetailsPanelProps) => {
  const { conversationId: contextConversationId } = useConversationContext()
  const conversationItemRoute = useInject<RouteDef>(WebuiIocKey.routes.conversationItem)
  const { hasRole: canEdit } = useRole({ roleName: chatPermissions.canEdit.value })
  // `customer.webui` is not importable from a module webui, so the widget is injected.
  const CustomerWidget = useInjectComponent<CustomerWidgetProps>(WebuiIocKey.customer.customerWidget)
  // One open flag per branch: a save that links the conversation swaps the branches, and a shared flag would
  // open the popover on the new widget.
  const [showLinkedPopover, toggleLinkedPopover] = useToggle(false)
  const [showUnlinkedPopover, toggleUnlinkedPopover] = useToggle(false)
  const conversationId = conversationIdProp ?? contextConversationId

  // One document read for the whole rail, keyed on the conversation's external id — the route's
  // own `_id` on this mount — never on `documentId`, which names the business document elsewhere.
  // `nextFetchPolicy` is `cache-first`, not the shared `cache-only`
  // (libs/common/hook/src/useList/useBuildListQueryOptions.ts:74): the thread's read
  // (libs/chat/common/src/hooks/useChatMessage.ts:55-59) has this read's root field and variables, so its
  // response is a cache write, and under `cache-only` that write reports `loading` false while this request
  // is still pending — the rail would read "Conversation not found." until it lands.
  const {
    document: chat,
    loading,
    error,
  } = useDocument<Chat>({
    query: conversationDetailsQuery,
    filter: { externalId: conversationId },
    options: { queryOptions: { nextFetchPolicy: 'cache-first' } },
  })

  const [addCc, , { error: addCcError }] = useMutation<Chat, AddChatCcArgs>({
    query: addChatCcMutation,
  })
  useError(addCcError)
  const [removeCc, , { error: removeCcError }] = useMutation<Chat, RemoveChatCcArgs>({
    query: removeChatCcMutation,
  })
  useError(removeCcError)

  // The thread's consolidated file list, flattened from the same messages that render files
  // inline — hidden with the section below while the conversation carries no files.
  const { messages } = useChatMessage({ conversationId })
  const attachments = React.useMemo(() => collectChatAttachments(messages), [messages])

  // The CC list has no single write; diff the submitted list against the current one and add or
  // remove each address through its own operation.
  const onUpdateCc = async ({ input }: { input?: { ccEmails?: string[] } }) => {
    const submitted = input?.ccEmails ?? []
    const current = chat?.ccEmails ?? []
    const added = difference(submitted, current)
    const removed = difference(current, submitted)
    for (const email of added) {
      await addCc({ input: { chatId: chat?._id ?? '', email } })
    }
    for (const email of removed) {
      await removeCc({ input: { chatId: chat?._id ?? '', email } })
    }
    return { errors: null }
  }

  const validateCc = async (emails: string[]) => {
    const invalid = (emails ?? []).filter((email) => !isValidEmail(email))
    if (!isEmpty(invalid)) {
      throw new Error(`Invalid email address: ${invalid.join(', ')}`)
    }
  }

  // Linked needs both the flag and a requester. A conversation that resolved no customer — a suspicious or
  // spam-listed submission, a vendor's or staff member's address — keeps the sender rows and [Link contact].
  const isLinked = !!chat?.isRequesterLinked && !isEmpty(chat?.requester?._id)

  return (
    <DataSuspense
      loading={loading}
      error={error}
      isEmpty={!chat}
      emptyComponent={<EmptyContent message="Conversation not found." />}
    >
      <Stack gap={2} className="conversation-details-rail">
        {/* A real `Card`, the same shape as the production item's rail
            (apps/panelpunches/admin/src/view/productions/item/Layout.tsx): its own body surface on the
            sidebar container's grey, and `Card.Body`'s padding alone — `Box` stacks `p-3` and `py-3`
            around its content (libs/webui/util/src/Box/index.tsx:5,17). */}
        <Card>
          {/* Rows lay out label-beside-value, following the project item rail
              (apps/panelpunches/admin/src/view/projects/item/Layout.tsx) instead of stacking — bar the
              two many-entry rows below. */}
          <Card.Body>
            <ShowIf condition={isLinked}>
              <div className="pb-2 border-bottom">
                <HoverContainer className="hstack gap-3 align-items-start">
                  <div style={{ minWidth: 0 }}>
                    <CustomerWidget
                      profile={chat?.requester?.profile}
                      status={chat?.requester?.status}
                      company={chat?.requester?.company}
                      phone={chat?.requester?.phone}
                      contactId={chat?.requester?._id}
                      userType={chat?.requester?.type}
                    />
                  </div>
                  <HoverMenuContainer pinned={showLinkedPopover} className="flex-shrink-0 text-nowrap">
                    <ConversationCustomerPopover
                      chat={chat}
                      linked
                      show={showLinkedPopover}
                      onClose={() => toggleLinkedPopover(false)}
                    >
                      {/* Disabled, not hidden, without the edit permission — the title names why. */}
                      <BusyButtonLink
                        text="Edit"
                        icon="edit"
                        title={canEdit ? 'Edit information' : 'Editing is disabled'}
                        disabled={!canEdit}
                        onClick={() => toggleLinkedPopover(true)}
                      />
                    </ConversationCustomerPopover>
                  </HoverMenuContainer>
                </HoverContainer>
              </div>
            </ShowIf>
            <ShowIf condition={!isLinked}>
              <div className="pb-2 border-bottom">
                <ConversationCustomerPopover
                  chat={chat}
                  linked={false}
                  show={showUnlinkedPopover}
                  onClose={() => toggleUnlinkedPopover(false)}
                >
                  <BusyButtonLink
                    text="[Link contact]"
                    icon="user-plus"
                    title={canEdit ? undefined : 'Editing is disabled'}
                    disabled={!canEdit}
                    onClick={() => toggleUnlinkedPopover(true)}
                  />
                </ConversationCustomerPopover>
              </div>
              <ShowIf condition={!!chat?.sender?.profile?.name}>
                <InlineDisplayContent label="Name" horizontalLayout>
                  {chat?.sender?.profile?.name}
                </InlineDisplayContent>
              </ShowIf>
              <ShowIf condition={!!chat?.sender?.company}>
                <InlineDisplayContent label="Company" horizontalLayout>
                  {chat?.sender?.company}
                </InlineDisplayContent>
              </ShowIf>
              <InlineDisplayContent label="Email" horizontalLayout>
                {chat?.sender?.profile?.email}
              </InlineDisplayContent>
              <ShowIf condition={!!chat?.sender?.phone}>
                <InlineDisplayContent label="Phone" horizontalLayout>
                  {chat?.sender?.phone}
                </InlineDisplayContent>
              </ShowIf>
            </ShowIf>
            {/* One panel for the whole thread — attachments, copied recipients and properties
                join the identity rows above as plain rows; each row's own bottom border
                (libs/webui/util/src/Block/LabelValueBlock.tsx) is the only divider. */}
            <ShowIf condition={!isEmpty(attachments)}>
              <StackedRow>
                <InlineDisplayContent label="Attachments">
                  <div className="text-break">
                    <ChatAttachments attachments={attachments} />
                  </div>
                </InlineDisplayContent>
              </StackedRow>
            </ShowIf>
            <StackedRow>
              <TagsInlineInput
                name="ccEmails"
                label="Copied to"
                canEdit={canEdit}
                initialValues={{ _id: chat?._id, ccEmails: chat?.ccEmails ?? [] }}
                onUpdate={onUpdateCc}
                validate={validateCc}
                inputProps={{ placeholder: 'Add an email address' }}
              />
            </StackedRow>
            <InlineDisplayContent label="Code" horizontalLayout>
              {chat?.code}
            </InlineDisplayContent>
            <InlineDisplayContent label={conversationListFieldConfig.lastMessageAt.displayName} horizontalLayout>
              <LocalDateTime value={chat?.lastMessageAt} />
            </InlineDisplayContent>
            <InlineDisplayContent label={conversationListFieldConfig.createdAt.displayName} horizontalLayout>
              <LocalDateTime value={chat?.createdAt} />
            </InlineDisplayContent>
            {/* The source item this conversation was opened from. The host supplies the
                code link (its document routes are product-specific), so an unattached
                conversation renders no row at all instead of an empty one. */}
            <ShowIf condition={!!chat?.document}>
              <InlineDisplayContent label="Related document" horizontalLayout>
                {renderDocumentCell({ document: chat?.document, metadata: chat?.metadata })}
              </InlineDisplayContent>
            </ShowIf>
            <SelectInlineInput
              name="assignee"
              label="Assignee"
              canEdit={canEdit}
              horizontalLayout
              selectComponent={AssigneeSelect}
              // `SelectInlineInput` matches its display option out of `selectOptions`, which an async
              // `AssigneeSelect` never has — so the assignee is pre-shaped into a `SelectOption` here.
              initialValues={{
                _id: chat?._id,
                assignee: chat?.assignee
                  ? { ...chat.assignee, value: chat.assignee._id, label: chat.assignee.profile?.name }
                  : undefined,
              }}
              mutationConfig={{ query: assignChatMutation }}
              // react-select calls its `onKeyDown` prop BEFORE it selects the focused option, so an
              // Enter-to-save would commit the value from before the pick. The select owns Enter;
              // blurring the field is what commits.
              saveOnEnterKey={false}
              updateParams={({ _id, updatedValue }) => ({
                input: { chatId: _id, assigneeId: updatedValue?._id ?? null } as AssignChatArgs,
              })}
              displayContent={(option: SelectOption) => option?.label}
              emptyContent={<AssignToMeLink chatId={chat?._id ?? ''} />}
              inputProps={{ isClearable: true, placeholder: 'Unassigned' }}
            />
            <TagsInlineInput
              name="tags"
              label="Tags"
              canEdit={canEdit}
              horizontalLayout
              horizontalFormLabelWidth={4}
              initialValues={{ _id: chat?._id, tags: chat?.tags ?? [] }}
              mutationConfig={{ query: updateChatTagsMutation }}
              updateParams={({ _id, updatedValue }) => ({
                input: { chatId: _id, tags: updatedValue } as UpdateChatTagsArgs,
              })}
            />
            <TextAreaInlineInput
              name="remarks"
              label="Remarks"
              canEdit={canEdit}
              horizontalLayout
              initialValues={{ _id: chat?._id, remarks: chat?.remarks ?? '' }}
              mutationConfig={{ query: updateChatRemarksMutation }}
              updateParams={({ _id, updatedValue }) => ({
                input: { chatId: _id, remarks: updatedValue } as UpdateChatRemarksArgs,
              })}
              inputProps={{ rows: 3 }}
            />
            <ShowIf condition={!!chat?.continuedFrom}>
              <InlineDisplayContent label="Continued from" horizontalLayout>
                <Link
                  to={conversationItemRoute.altPath({
                    _id: chat?.continuedFrom?.externalId ?? chat?.continuedFrom?._id ?? '',
                  })}
                >
                  {chat?.continuedFrom?.code}
                </Link>
              </InlineDisplayContent>
            </ShowIf>
            <ShowIf condition={chat?.status === ChatStatusEnum.Cancelled}>
              <InlineDisplayContent label="Cancel reason" horizontalLayout>
                {
                  // A missing reason is an agent's own cancellation — the transition writes that
                  // member when none arrives, so the rail never renders a blank reason.
                  chatCancelReasonDescription[chat?.cancelReason ?? ChatCancelReasonEnum.AgentCancellation]
                }
              </InlineDisplayContent>
            </ShowIf>
          </Card.Body>
        </Card>
      </Stack>
    </DataSuspense>
  )
}
